import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { fmtNumber } from '../lib/format';
import { SectionHeader, EmptyState } from '../components/ui';

interface BookingsRow {
  account_manager: string;
  count: number;
  total_sgd: number;
}

interface Aggregation {
  rangeStart: string;
  rangeEnd: string;
  rows: BookingsRow[];
  unattributed: { count: number; total_sgd: number };
}

function fmtSgd(n: number): string {
  if (n == null || !Number.isFinite(n)) return 'S$0';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    maximumFractionDigits: 0,
  }).format(n);
}

function BookingsTable({
  agg,
  emptyMessage,
}: {
  agg: Aggregation;
  emptyMessage: string;
}) {
  const totalCount = agg.rows.reduce((s, r) => s + r.count, 0) + agg.unattributed.count;
  const totalSgd =
    agg.rows.reduce((s, r) => s + r.total_sgd, 0) + agg.unattributed.total_sgd;

  if (totalCount === 0) {
    return <div className="text-xs text-white/40 py-4">{emptyMessage}</div>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-white/40 text-xs border-b border-border-subtle">
          <th className="text-left font-normal pb-2 w-1/2">Account manager</th>
          <th className="text-right font-normal pb-2">Bookings</th>
          <th className="text-right font-normal pb-2">Total (SGD)</th>
        </tr>
      </thead>
      <tbody>
        {agg.rows.map((r) => (
          <tr
            key={r.account_manager}
            className="border-b border-border-subtle/50 last:border-0"
          >
            <td className="py-2.5">{r.account_manager}</td>
            <td className="text-right py-2.5">{fmtNumber(r.count)}</td>
            <td className="text-right py-2.5 font-medium">{fmtSgd(r.total_sgd)}</td>
          </tr>
        ))}
        {agg.unattributed.count > 0 && (
          <tr className="border-b border-border-subtle/50 text-white/60">
            <td className="py-2.5 italic">
              Unattributed{' '}
              <span className="text-xs text-white/40">
                (no name matched in Reference / Title / line items)
              </span>
            </td>
            <td className="text-right py-2.5">{fmtNumber(agg.unattributed.count)}</td>
            <td className="text-right py-2.5">{fmtSgd(agg.unattributed.total_sgd)}</td>
          </tr>
        )}
        <tr className="border-t-2 border-border-subtle">
          <td className="pt-3 font-medium">Total</td>
          <td className="text-right pt-3 font-medium">{fmtNumber(totalCount)}</td>
          <td className="text-right pt-3 font-medium">{fmtSgd(totalSgd)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export default function FinanceSection() {
  const [mtd, setMtd] = useState<Aggregation | null>(null);
  const [trailing12, setTrailing12] = useState<Aggregation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .bookingsSummary(['HC_SG', 'HC_BALI'])
      .then((r) => {
        setMtd(r.mtd);
        setTrailing12(r.trailing12);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-xs text-white/40 py-12 text-center">Loading finance data…</div>;
  }
  if (error) {
    return (
      <div className="text-xs text-red-400 py-12 text-center">
        Couldn't load finance data: {error}
      </div>
    );
  }
  if (!mtd || !trailing12) {
    return (
      <EmptyState
        title="Finance — Xero"
        message="Bookings will populate once the Xero OAuth flow is complete and the worker has run at least once. See worker/XERO_OAUTH_SETUP.md."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="raised-card">
        <SectionHeader
          title="Bookings closed — month to date"
          hint={`accepted quotes ${mtd.rangeStart} → ${mtd.rangeEnd}, HC_SG + HC_BALI, totals in SGD`}
        />
        <BookingsTable
          agg={mtd}
          emptyMessage={`No accepted quotes recorded yet for ${mtd.rangeStart} → ${mtd.rangeEnd}.`}
        />
      </div>

      <div className="raised-card">
        <SectionHeader
          title="Bookings closed — last 12 months"
          hint={`accepted quotes ${trailing12.rangeStart} → ${trailing12.rangeEnd}, HC_SG + HC_BALI, totals in SGD`}
        />
        <BookingsTable
          agg={trailing12}
          emptyMessage={`No accepted quotes recorded for the trailing 12 months. The Worker has only been ingesting Xero recently — older history may need a manual backfill.`}
        />
        <div className="text-xs text-white/40 mt-3 leading-relaxed">
          History is limited to weeks the cron has run. To populate older months, an
          explicit backfill is needed (extending the date window in xero.ts and
          triggering /run-now once with a wider range). Account-manager attribution comes
          from the parser in worker/src/sources/xero.ts — add a name there to start
          tracking it.
        </div>
      </div>
    </div>
  );
}
